import { useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faEnvelope,
  faNewspaper,
  faBullhorn,
  faLock,
  faEye,
  faEyeSlash,
  faCamera,
  faMicrophone,
  faUsers,
  faShieldAlt,
  faGavel,
  faBriefcase,
  faGraduationCap
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { saveUserData, redirectToDashboard } from "../../utils/auth";

const AdvocateLogin = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Frontend validation
    if (!formData.email || !formData.password) {
      setError("Email and password are required");
      setIsLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      setIsLoading(false);
      return;
    }

    // Note: Register page requires @gmail.com, but login accepts any valid email
    // This allows flexibility if email domain restrictions change in the future

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/advocate/login`,
        {
          email: formData.email,
          password: formData.password
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const data = response.data;
      if (data?.token) {
        // Save user data
        const userData = {
          ...data.user,
          token: data.token,
          role: 'advocate'
        };
        saveUserData(userData);
        // Also save user separately for easy access
        localStorage.setItem('user', JSON.stringify(userData));

        toast.success("Login successful!");

        // Get redirect path if it exists
        const redirectPath = localStorage.getItem('redirectPath');
        localStorage.removeItem('redirectPath'); // Clear it after use

        // Redirect to dashboard
        setTimeout(() => {
          redirectToDashboard('advocate', navigate, redirectPath || '/advocate/dashboard');
        }, 500);
      } else {
        setError(data?.message || "Login failed");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError("Invalid email or password. Please check your credentials.");
      } else if (err.response?.status === 404) {
        setError("User not found with this email. Please register first.");
      } else if (err.response?.status === 403) {
        setError("Account not verified. Please verify your email first.");
      } else if (err.response?.status === 500) {
        setError("Internal server error. Please try again later.");
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else if (!err.response) {
        setError("Network error. Please check your internet connection.");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8 pt-20">
      <div className="w-full max-w-6xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
        <div className="flex flex-col lg:flex-row">
          {/* Left side - Company Branding */}
          <div className="hidden lg:block lg:w-5/12 bg-gradient-to-br from-orange-700 via-amber-600 to-orange-900 relative overflow-hidden text-white">
            {/* Background Pattern Overlay */}
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`, backgroundSize: '24px 24px' }}></div>

            {/* Animated Background Elements */}
            <motion.div
              animate={{
                scale: [1, 1.2, 1],
                rotate: [0, 90, 0],
                opacity: [0.05, 0.1, 0.05]
              }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl -mr-32 -mt-32"
            ></motion.div>

            <motion.div
              animate={{
                scale: [1, 1.5, 1],
                x: [0, 50, 0],
                opacity: [0.1, 0.2, 0.1]
              }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
              className="absolute bottom-0 left-0 w-64 h-64 bg-orange-400 rounded-full blur-3xl -ml-20 -mb-20"
            ></motion.div>

            <div className="relative flex flex-col justify-between h-full p-12 z-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-8">
                  <div className="w-2 h-2 bg-orange-300 rounded-full animate-pulse shadow-[0_0_8px_rgba(253,186,116,0.8)]"></div>
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-orange-100">Legal Excellence Portal</span>
                </div>

                <div className="w-20 h-20 mb-8 bg-gradient-to-tr from-white/20 to-white/5 backdrop-blur-xl border border-white/30 rounded-[2rem] flex items-center justify-center shadow-2xl relative group overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-tr from-orange-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <FontAwesomeIcon icon={faGavel} className="text-3xl text-white drop-shadow-md group-hover:scale-110 transition-transform duration-500" />
                </div>

                <h2 className="text-4xl font-[1000] tracking-tight mb-4 leading-[1.1] drop-shadow-sm">
                  Practice of <span className="text-orange-300">Justice</span>
                </h2>
                <p className="text-orange-50/80 font-medium tracking-wide text-base max-w-xs leading-relaxed">
                  The professional platform for advocates to manage cases, build specializations, and drive legal impact.
                </p>
              </motion.div>

              <div className="space-y-6">
                <div className="space-y-4">
                  {[
                    { icon: faGavel, title: "Legal Practice", desc: "Manage your complex cases", color: "orange", delay: 0.1 },
                    { icon: faBriefcase, title: "Specialization", desc: "Focused legal expertise", color: "blue", delay: 0.2 },
                    { icon: faGraduationCap, title: "Experience", desc: "Showcase career milestones", color: "purple", delay: 0.3 }
                  ].map((item, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: item.delay, duration: 0.5 }}
                      className="group flex items-center gap-4 bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/10 hover:border-white/20 rounded-2xl p-4 transition-all duration-300 cursor-default"
                    >
                      <div className={`w-12 h-12 bg-${item.color}-500/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500`}>
                        <FontAwesomeIcon icon={item.icon} className={`text-${item.color}-300 text-xl`} />
                      </div>
                      <div>
                        <span className="font-black tracking-wide text-sm block group-hover:text-orange-300 transition-colors uppercase">{item.title}</span>
                        <p className="text-[11px] text-orange-100/60 uppercase tracking-widest font-bold font-sans">{item.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="pt-8 border-t border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3 text-orange-100/60">
                    <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                      <FontAwesomeIcon icon={faShieldAlt} className="text-xs" />
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-[0.2em]">Verified Professional</span>
                  </div>
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map(i => (
                      <div key={i} className="w-6 h-6 rounded-full border-2 border-orange-800 bg-orange-600 flex items-center justify-center text-[8px] font-bold">
                        {i}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Login Form */}
          <div className="w-full lg:w-3/5 p-6 lg:p-8">
            <div className="text-center mb-8 group">
              <h1 className="text-3xl font-[1000] text-slate-900 tracking-tighter mb-2 uppercase leading-none">
                Sign In
              </h1>
              <div className="flex justify-center mb-4">
                <span className="text-2xl font-[1000] text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-amber-500 uppercase">Advocate Portal</span>
              </div>
              <div className="w-16 h-1.5 bg-gradient-to-r from-orange-500 to-amber-500 mx-auto rounded-full mb-4 transition-all duration-500 group-hover:w-24"></div>
              <p className="text-sm font-bold text-slate-500 tracking-wide flex items-center justify-center gap-2">
                <span className="w-1 h-4 bg-orange-500 rounded-full"></span>
                Access your professional legal dashboard
              </p>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-50 border border-red-200 text-red-700 p-2 rounded-md text-xs font-medium"
                >
                  {error}
                </motion.div>
              )}

              <div>
                <label htmlFor="email" className="block text-xs font-medium text-gray-600 mb-1">
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faEnvelope} className="text-gray-400 text-xs" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="block w-full pl-8 pr-2 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                    placeholder="Enter your email address"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-xs font-medium text-gray-600 mb-1">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faLock} className="text-gray-400 text-xs" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="block w-full pl-8 pr-10 py-2 border border-gray-200 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="text-xs" />
                  </button>
                </div>
              </div>

              <div className="mt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg shadow-md text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 focus:ring-1 focus:ring-blue-500 focus:ring-offset-1 transition-all ${isLoading ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Logging in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>
              </div>

              <div className="text-center mt-3">
                <span className="text-xs text-gray-600">Don't have an account? </span>
                <Link to="/advocate/register" className="text-blue-600 hover:text-blue-700 font-medium text-xs">
                  Sign up
                </Link>
              </div>
            </form>

            {/* Platform Features */}
            <div className="mt-6 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center mb-3">Platform Other</p>

              {/* Quick Login Buttons */}
              <div className="mt-4 space-y-2">
                <p className="text-xs text-gray-500 text-center mb-2">Quick Access</p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                  <Link
                    to="/press/login"
                    className="flex items-center justify-center py-1.5 px-2 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                  >
                    <FontAwesomeIcon icon={faCamera} className="text-blue-500 text-xs mr-1" />
                    <span className="text-xs text-blue-600 font-medium">Press</span>
                  </Link>

                  <Link
                    to="/raise/login"
                    className="flex items-center justify-center py-1.5 px-2 bg-purple-50 hover:bg-purple-100 rounded-md transition-colors"
                  >
                    <FontAwesomeIcon icon={faBullhorn} className="text-purple-500 text-xs mr-1" />
                    <span className="text-xs text-purple-600 font-medium">Raise</span>
                  </Link>

                  <Link
                    to="/podcast/login"
                    className="flex items-center justify-center py-1.5 px-2 bg-green-50 hover:bg-green-100 rounded-md transition-colors"
                  >
                    <FontAwesomeIcon icon={faUsers} className="text-green-500 text-xs mr-1" />
                    <span className="text-xs text-green-600 font-medium">Podcast</span>
                  </Link>

                  <Link
                    to="/advocate/login"
                    className="flex items-center justify-center py-1.5 px-2 bg-orange-50 hover:bg-orange-100 rounded-md transition-colors"
                  >
                    <FontAwesomeIcon icon={faGavel} className="text-orange-500 text-xs mr-1" />
                    <span className="text-xs text-orange-600 font-medium">Advocate</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvocateLogin;
